import { useCallback, useSyncExternalStore } from 'react';

/**
 * Which BF-axis grounding the matrix asks the API for — the old/new switch.
 *
 *   `glob`  — today's model: a file joins a business function because a cluster
 *             glob matched its path.
 *   `facet` — a file joins because it belongs to a *facet*, and the facet resolves
 *             to its consuming BFs through the dependency graph.
 *
 * Same storage pattern as `active-repo.ts` (localStorage + `useSyncExternalStore`),
 * for the same reasons: the choice survives a reload, stays in sync across tabs,
 * and needs no context provider.
 *
 * It is a READ-PATH preference, not a setting. Nothing is written back, no catalog
 * changes, and flipping it costs one extra server-side cache entry — so it is safe
 * to leave on, off, or flip repeatedly while comparing.
 *
 * The server decides what it can honour: an org with no facet rules serves `glob`
 * whatever is requested and reports `grounding.applied` in the response. Trust that
 * field over this one when labelling anything on screen — otherwise the UI will
 * eventually claim a grounding the server never applied.
 */

export type MatrixGrounding = 'glob' | 'facet';

const KEY = 'ta_matrix_grounding';
const EVENT = 'ta:matrix-grounding';
const DEFAULT: MatrixGrounding = 'glob';

export function getStoredGrounding(): MatrixGrounding {
  try {
    return localStorage.getItem(KEY) === 'facet' ? 'facet' : DEFAULT;
  } catch {
    return DEFAULT;
  }
}

export function setStoredGrounding(grounding: MatrixGrounding): void {
  try {
    localStorage.setItem(KEY, grounding);
    window.dispatchEvent(new Event(EVENT));
  } catch {
    // ignore (private mode, etc.)
  }
}

function subscribe(listener: () => void): () => void {
  const onStorage = (e: StorageEvent) => {
    if (e.key === KEY) listener();
  };
  window.addEventListener('storage', onStorage);
  window.addEventListener(EVENT, listener);
  return () => {
    window.removeEventListener('storage', onStorage);
    window.removeEventListener(EVENT, listener);
  };
}

const getSnapshot = (): MatrixGrounding => getStoredGrounding();
/** SSR/prerender must render the default, or the markup mismatches on hydrate. */
const getServerSnapshot = (): MatrixGrounding => DEFAULT;

export function useMatrixGrounding(): {
  grounding: MatrixGrounding;
  setGrounding: (g: MatrixGrounding) => void;
} {
  const grounding = useSyncExternalStore(subscribe, getSnapshot, getServerSnapshot);
  const setGrounding = useCallback((g: MatrixGrounding) => setStoredGrounding(g), []);
  return { grounding, setGrounding };
}
